import {gql} from '@apollo/client';
import {useState} from 'react';

import {assertNotNullAndReturn} from 'Asserts';
import {InaccessibleInfoNode, ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {AllPMEIsInStudyQuery_study_StudyNode_procedureRuns_ProcedureRunNode as ProcedureRunNode} from 'studies/typed/process-development/unit-op-linking-flowchart/__generated/AllPMEIsInStudyQuery';
import {
  AllPMEIsInStudyQuery,
  AllPMEIsInStudyQueryVariables,
} from 'studies/typed/process-development/unit-op-linking-flowchart/__generated/AllPMEIsInStudyQuery';

import {UnitOpLinkEdge} from './UnitOpLinkingFlowchartUtils';

export const ALL_PMEIS_IN_STUDY_QUERY = gql`
  query AllPMEIsInStudyQuery($studyId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        procedureRuns {
          ... on ProcedureRunNode {
            id
            name
            displayId
            procedureType
            currentProcedureFlowchartVersion {
              id
              nodes {
                id
              }
            }
            methodExecutionInstances {
              ... on ProcedureMethodExecutionInstanceNode {
                id
                planMethodDefinitionVersion {
                  ... on ProcedurePlanMethodDefinitionVersionNode {
                    id
                    name
                  }
                }
                targetProcedureMethodExecutionInstanceAssociations {
                  ... on ProcedureMethodExecutionInstanceAssociationNode {
                    id
                    sourceProcedureMethodExecutionInstance {
                      id
                    }
                    targetProcedureMethodExecutionInstance {
                      id
                    }
                  }
                }
                sourceProcedureMethodExecutionInstanceAssociations {
                  ... on ProcedureMethodExecutionInstanceAssociationNode {
                    id
                    sourceProcedureMethodExecutionInstance {
                      id
                    }
                    targetProcedureMethodExecutionInstance {
                      id
                    }
                  }
                }
                procedureRunConditionReplicate {
                  ... on ProcedureRunConditionReplicateNode {
                    id
                    replicateNumber
                    procedureRunCondition {
                      ... on ProcedureRunConditionNode {
                        id
                        name
                      }
                    }
                  }
                }
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface UnitOperationFields {
  pmeiId: string;
  procedureRunId: string;
  procedureRunName: string | null;
  procedureRunOrderKey: number;
  /**
   * The display ID of the procedure run. This is only used for the source procedure run,
   * and is not used for the destination procedure run.
   */
  procedureRunDisplayId: string | null;
  methodName: string;
  methodOrderKey: number;
  conditionName: string;
  conditionOrderKey: number;
  replicateNumber: number;
  procedureType: ProcedureType;
}

interface ExistingDestinationInfo {
  procedureRunId: string;
  pmeis?: never;
}
interface NewDestinationInfo {
  pmeis: Array<UnitOperationFields>;
  procedureRunId?: never;
}

export enum UnitOpLinkingStateStatus {
  QUERY_ERROR = 'QUERY_ERROR',
  QUERY_PENDING = 'QUERY_PENDING',
  STUDY_INACCESSIBLE = 'STUDY_INACCESSIBLE',
  SUCCESS = 'SUCCESS',
}

export interface SuccessfulQueryUnitOpLinkingState {
  status: UnitOpLinkingStateStatus.SUCCESS;
  sourcePMEIs: Array<UnitOperationFields>;
  destinationPMEIs: Array<UnitOperationFields>;
  initialLinks: Array<UnitOpLinkEdge>; // Pre-existing links queried before user has made any edits
  newLinks: Array<UnitOpLinkEdge> | null; // Link state after the user makes changes, or null if no changes
  setNewLinks: (links: Array<UnitOpLinkEdge>) => void;
}

interface InaccessibleStudyUnitOpLinkingState {
  status: UnitOpLinkingStateStatus.STUDY_INACCESSIBLE;
  inaccessibleStudy: InaccessibleInfoNode;
}

interface QueryErrorUnitOpLinkingState {
  status: Exclude<
    UnitOpLinkingStateStatus,
    UnitOpLinkingStateStatus.SUCCESS | UnitOpLinkingStateStatus.STUDY_INACCESSIBLE
  >;
  inaccessibleStudy?: never;
}

export type NotSuccessfulQueryUnitOpLinkingState =
  | InaccessibleStudyUnitOpLinkingState
  | QueryErrorUnitOpLinkingState;

/**
 * Fetches data from the study for use by the UnitOpLinkingFlowchartEditor. This hook is used both during
 * experiment creation and after experiment creation. In the during experiment creation case, the destination
 * PMEIs are passed in via state from the parent component. In the after experiment creation case, the PMEIs
 * are fetched from the current procedureRunId.
 */
export default function useUnitOpLinkingState(
  studyId: string,
  destinationInfo: ExistingDestinationInfo | NewDestinationInfo
): SuccessfulQueryUnitOpLinkingState | NotSuccessfulQueryUnitOpLinkingState {
  const result = useBenchlingQuery<AllPMEIsInStudyQuery, AllPMEIsInStudyQueryVariables>(
    ALL_PMEIS_IN_STUDY_QUERY,
    {
      variables: {studyId},
      // renderCachedDataWhenRefetching must be false here to avoid use of incomplete data from other queries
      // after successful creation of a new run/experiment (at least until apollo-client is upgraded to 3.10+)
      // See issue "useQuery can unexpectedly return partial data"
      renderCachedDataWhenRefetching: false,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
    }
  );

  // We need to run the useState hook for links regardless of whether or not the query was successful, to
  // meet the React constraint that the same number of hooks is run (in the same order) every render.
  // In the case that the query was not successful, we initialize to an empty array, and this value is unused.
  let initialLinks: Array<UnitOpLinkEdge> = [];
  let orderedSourcePMEIs: Array<UnitOperationFields> = [];
  let orderedDestinationPMEIs: Array<UnitOperationFields> = [];
  if (result.type === QueryResultType.SUCCESS && !isInaccessible(result.data.study)) {
    const procedureRuns = accessibleObjs(result.data.study.procedureRuns);
    orderedSourcePMEIs = getOrderedSourcePMEIs(procedureRuns, destinationInfo);
    orderedDestinationPMEIs = getOrderedDestinationPMEIs(procedureRuns, destinationInfo);
    initialLinks = getExistingLinks(procedureRuns, orderedSourcePMEIs, orderedDestinationPMEIs);
  }
  const [newLinks, setNewLinks] = useState<Array<UnitOpLinkEdge> | null>(null);

  if (result.type === QueryResultType.ERROR) {
    return {
      status: UnitOpLinkingStateStatus.QUERY_ERROR,
    };
  } else if (result.type === QueryResultType.PENDING) {
    return {
      status: UnitOpLinkingStateStatus.QUERY_PENDING,
    };
  } else if (isInaccessible(result.data.study)) {
    return {
      status: UnitOpLinkingStateStatus.STUDY_INACCESSIBLE,
      inaccessibleStudy: result.data.study,
    };
  } else {
    // Query was successful and study is accessible.
    return {
      status: UnitOpLinkingStateStatus.SUCCESS,
      sourcePMEIs: orderedSourcePMEIs,
      destinationPMEIs: orderedDestinationPMEIs,
      initialLinks,
      newLinks,
      setNewLinks,
    };
  }
}

function getOrderedSourcePMEIs(
  procedureRuns: Array<ProcedureRunNode>,
  destinationInfo: ExistingDestinationInfo | NewDestinationInfo
): Array<UnitOperationFields> {
  const sourceRuns = procedureRuns.filter(
    (procedureRun) => procedureRun.id !== destinationInfo.procedureRunId
  );
  return sourceRuns.flatMap((procedureRun, runIndex) => {
    const orderedPMDVIds = procedureRun.currentProcedureFlowchartVersion.nodes.map(({id}) => id);
    return procedureRun.methodExecutionInstances.map((pmei, pmeiIndex) => ({
      pmeiId: pmei.id,
      procedureRunId: procedureRun.id,
      procedureRunName: procedureRun.name,
      procedureRunOrderKey: runIndex,
      procedureRunDisplayId: procedureRun.displayId,
      methodName: pmei.planMethodDefinitionVersion.name,
      methodOrderKey: orderedPMDVIds.indexOf(pmei.planMethodDefinitionVersion.id),
      conditionName: pmei.procedureRunConditionReplicate.procedureRunCondition.name,
      conditionOrderKey: pmeiIndex, // Assume PMEIs already correctly ordered w.r.t. conditions
      replicateNumber: pmei.procedureRunConditionReplicate.replicateNumber,
      procedureType: procedureRun.procedureType,
    }));
  });
}

function getOrderedDestinationPMEIs(
  procedureRuns: Array<ProcedureRunNode>,
  destinationInfo: ExistingDestinationInfo | NewDestinationInfo
): Array<UnitOperationFields> {
  // New destination, just use the provided PMEIs
  if (destinationInfo.pmeis != null) {
    return destinationInfo.pmeis;
  }
  // Existing destination, grab PMEIs from the right run
  const targetProcedureRun = assertNotNullAndReturn(
    procedureRuns.find((procedureRun) => procedureRun.id === destinationInfo.procedureRunId)
  );
  const orderedPMDVIds = targetProcedureRun.currentProcedureFlowchartVersion.nodes.map(({id}) => id);
  return targetProcedureRun.methodExecutionInstances.map((pmei, pmeiIndex) => ({
    pmeiId: pmei.id,
    procedureRunId: targetProcedureRun.id,
    procedureRunName: targetProcedureRun.name,
    procedureRunOrderKey: 0, // Only one run allowed as destination
    procedureRunDisplayId: targetProcedureRun.displayId,
    methodName: pmei.planMethodDefinitionVersion.name,
    methodOrderKey: orderedPMDVIds.indexOf(pmei.planMethodDefinitionVersion.id),
    conditionName: pmei.procedureRunConditionReplicate.procedureRunCondition.name,
    conditionOrderKey: pmeiIndex, // Assume PMEIs already correctly ordered w.r.t. conditions
    replicateNumber: pmei.procedureRunConditionReplicate.replicateNumber,
    procedureType: targetProcedureRun.procedureType,
  }));
}

function getExistingLinks(
  queriedProcedureRuns: Array<ProcedureRunNode>,
  orderedSourcePMEIs: Array<UnitOperationFields>,
  orderedDestinationPMEIs: Array<UnitOperationFields>
): Array<UnitOpLinkEdge> {
  const links: Array<UnitOpLinkEdge> = [];
  const sourcePMEIIds = orderedSourcePMEIs.map((sourcePMEI) => sourcePMEI.pmeiId);
  const destinationPMEIIds = orderedDestinationPMEIs.map((destinationPMEI) => destinationPMEI.pmeiId);
  queriedProcedureRuns.forEach((procedureRun) => {
    procedureRun.methodExecutionInstances.forEach((pmei) => {
      pmei.targetProcedureMethodExecutionInstanceAssociations.forEach((association) => {
        if (!isInaccessible(association)) {
          const sourcePmeiId = association.sourceProcedureMethodExecutionInstance.id;
          const targetPmeiId = association.targetProcedureMethodExecutionInstance.id;
          if (sourcePmeiId && targetPmeiId) {
            if (sourcePMEIIds.includes(sourcePmeiId) && destinationPMEIIds.includes(targetPmeiId)) {
              links.push({sourcePMEIId: sourcePmeiId, destinationPMEIId: targetPmeiId});
            }
          }
        }
      });
    });
  });
  return links;
}
